import React from 'react';
import Page from 'components/page/page';
import PageSection from 'components/page/page-section';
import PageHeader from 'components/page/page-header';
import PageCookie from 'components/page-cookie/page-cookie';
import PageFooter from 'components/page/page-footer';
import { Header1, P1 } from 'components/typography';
import lexemes from 'components/lexemes';
import { Columns, ColumnsItem } from 'components/columns';
import Cards, { CardsTypes } from 'components/cards/cards';
import Card from 'components/card/card';
import Tools from 'components/tools/tools';
// import ToggleCardCta from 'components/toggle-card/toggle-card-cta';
import UpdatesCard from 'components/updates-card/updates-card';
import HelplineSection from 'components/helpline/helpline-section';
import PageVideoBlock from 'components/page-video-block/page-video-block';
import SearchField from 'components/search-field/search-field';
import * as Scroll from 'react-scroll';
import Image from 'next/image';
import useWindowSize from 'lib/use-window-resize';

import {
    Guides as GuidesType,
    Tools as ToolsType,
    Helplines as HelplinesType,
    Updates as UpdatesType,
    Navigation,
} from 'schema/frontend/types';
import useIsomorphicLayoutEffect from 'lib/use-isomorphic-layout-effect';
import { setupProperPartnerId } from 'lib/handle-data';

type PageIndexPropsType = {
    guides: GuidesType;
    tools: ToolsType;
    helplines: HelplinesType;
    updates: UpdatesType;
    activeToolItemUrl?: string;
    navigation: Navigation;
    video?: string;
};

const PageIndex: React.FC<PageIndexPropsType> = ({
    tools,
    guides,
    helplines,
    updates,
    activeToolItemUrl,
    navigation,
    video,
}) => {
    const [activeToolId, setActiveToolId] = React.useState<number | undefined>(undefined);
    const toolsListRef = React.useRef<HTMLUListElement>(null);
    const size = useWindowSize();
    const [heroWidth, setHeroWidth] = React.useState(2880);
    const [heroHeight, setHeroHeight] = React.useState(1800);
    const [heroImage, setHeroImage] = React.useState('/safety-centre/assets/hero-bg-3.jpg');
    const sectionRef = React.useRef<HTMLDivElement>(null);
    const scrollToHandler = (element: string) => {
        const scroller = Scroll.scroller;
        const scrollValue =
            -1 *
                parseInt(
                    getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
                    10
                ) +
                10 || -58;
        scroller.scrollTo(element, {
            offset: scrollValue,
        });
    };

    useIsomorphicLayoutEffect(() => {
        if (window.location.hash === '#guides') {
            scrollToHandler('guides');
        }
        if (window.location.hash === '#tools') {
            scrollToHandler('tools');
        }
        if (window.location.hash === '#helplines') {
            scrollToHandler('helplines');
        }
        if (window.location.hash === '#updates') {
            scrollToHandler('updates');
        }
    }, []);

    useIsomorphicLayoutEffect(() => {
        if (size?.width && size?.width < 768) {
            setHeroImage('/safety-centre/assets/hero-bg-mobile-3.jpg');
        } else {
            setHeroImage('/safety-centre/assets/hero-bg-3.jpg');
        }

        if (sectionRef.current && size?.height) {
            setHeroWidth(sectionRef.current.clientWidth);
            setHeroHeight(size?.height);
        }
    }, [size, sectionRef]);

    React.useEffect(() => {
        if (activeToolItemUrl && toolsListRef) {
            setActiveToolId(
                Array.prototype.indexOf.call(
                    toolsListRef.current?.children,
                    document.getElementById(activeToolItemUrl)
                )
            );
        }
    }, [activeToolItemUrl, toolsListRef]);

    return (
        <Page
            extraClass="page--index"
            header={<PageHeader bgColor="var(--color-primary-02)" navigation={navigation} />}
            footer={<PageFooter />}
            content={
                <>
                    {/* @ts-ignore */}
                    <Scroll.Element name="hero">
                        <PageSection extraClass="page__section--hero">
                            <div className="page__section-hero-media" ref={sectionRef}>
                                <Image
                                    // eslint-disable-next-line react/forbid-component-props
                                    className="page__section-hero-image"
                                    src={heroImage}
                                    alt=""
                                    // window.innerHeight
                                    // window.innerWidth
                                    width={heroWidth}
                                    height={heroHeight}
                                    style={{ objectFit: 'cover' }}
                                    priority={true}
                                />
                            </div>
                            <div className="page__section-hero-title">
                                <Header1
                                    color="var(--color-primary-01)"
                                    extraClass="is-centered"
                                    tag="h1"
                                >
                                    {lexemes.heroBlock.lxBlockTitle()}
                                </Header1>
                            </div>
                            <div className="page__search">
                                <SearchField />
                            </div>
                        </PageSection>
                    </Scroll.Element>

                    {/* @ts-ignore */}
                    <Scroll.Element name="video">
                        <PageSection
                            theme={PageSection.Theme.THREE}
                            hasPaddingEqual={true}
                            tag="section"
                        >
                            <PageVideoBlock video={video} />
                        </PageSection>
                    </Scroll.Element>

                    {guides ? (
                        <section>
                            {/* @ts-ignore */}
                            <Scroll.Element name="guides">
                                <PageSection theme={PageSection.Theme.ONE}>
                                    <Columns vAlignTop={true} hasMobileLayout={true}>
                                        <ColumnsItem size={ColumnsItem.size.fluid}>
                                            <Header1
                                                color="var(--color-primary-02)"
                                                isOneLine={true}
                                                tag="h2"
                                                id="guides"
                                            >
                                                {lexemes.guides.lxBlockTitle()}
                                            </Header1>
                                        </ColumnsItem>
                                        <ColumnsItem size={ColumnsItem.size.fluid}>
                                            <P1 color="var(--color-primary-02)">
                                                {lexemes.guides.lxBlockText()}
                                            </P1>
                                        </ColumnsItem>
                                    </Columns>
                                </PageSection>
                                <PageSection theme={PageSection.Theme.ONE} noTopPadding={true}>
                                    <Cards
                                        extraClass={guides.length > 2 ? undefined : 'cards--half'}
                                    >
                                        {guides.map((item, index) => {
                                            const { title, text, imageUrl, url, backgroundColor } =
                                                item;
                                            return (
                                                <React.Fragment key={index}>
                                                    <Card
                                                        title={title}
                                                        text={text}
                                                        imageUrl={imageUrl}
                                                        backgroundColor={backgroundColor}
                                                        url={`/safety-centre${url}`}
                                                        hasNoBottomPadding={true}
                                                    />
                                                </React.Fragment>
                                            );
                                        })}
                                    </Cards>
                                </PageSection>
                            </Scroll.Element>
                        </section>
                    ) : null}

                    {tools ? (
                        <section>
                            {/* @ts-ignore */}
                            <Scroll.Element name="tools">
                                <PageSection theme={PageSection.Theme.THREE}>
                                    <Columns
                                        vAlignTop={true}
                                        hasMobileLayout={true}
                                        extraGap={true}
                                    >
                                        <ColumnsItem size={ColumnsItem.size.fluid}>
                                            <Header1
                                                color="var(--color-primary-01)"
                                                isOneLine={true}
                                                tag="h2"
                                                id="tools"
                                            >
                                                {lexemes.tools.lxBlockTitle()}
                                            </Header1>
                                        </ColumnsItem>
                                        <ColumnsItem size={ColumnsItem.size.fluid}>
                                            <P1 color="var(--color-primary-01)">
                                                {setupProperPartnerId(lexemes.tools.lxBlockText())}
                                            </P1>
                                        </ColumnsItem>
                                    </Columns>
                                </PageSection>
                                <PageSection theme={PageSection.Theme.THREE} noTopPadding={true}>
                                    <Tools
                                        content={tools}
                                        activeItem={activeToolId}
                                        toolsListRef={toolsListRef}
                                    />
                                    {/* <ToggleCardCta url="#">{lexemes.tools.lxBlockCta()}</ToggleCardCta> */}
                                </PageSection>
                            </Scroll.Element>
                        </section>
                    ) : null}

                    {helplines ? (
                        <section>
                            {/* @ts-ignore */}

                            <Scroll.Element name="helplines">
                                <HelplineSection cards={helplines} />
                            </Scroll.Element>
                        </section>
                    ) : null}

                    {updates ? (
                        <section>
                            {/* @ts-ignore */}
                            <Scroll.Element name="updates">
                                <PageSection theme={PageSection.Theme.THREE}>
                                    <Columns
                                        vAlignTop={true}
                                        hasMobileLayout={true}
                                        extraGap={true}
                                    >
                                        <ColumnsItem size={ColumnsItem.size.fluid}>
                                            <Header1
                                                color="var(--color-primary-01)"
                                                isOneLine={true}
                                                tag="h2"
                                                id="updates"
                                            >
                                                {lexemes.updates.lxBlockTitle()}
                                            </Header1>
                                        </ColumnsItem>
                                        <ColumnsItem size={ColumnsItem.size.fluid}>
                                            <P1 color="var(--color-primary-01)">
                                                {lexemes.updates.lxBlockText()}
                                            </P1>
                                        </ColumnsItem>
                                    </Columns>
                                </PageSection>
                                <PageSection theme={PageSection.Theme.THREE} noTopPadding={true}>
                                    <Cards type={CardsTypes.UPDATES}>
                                        {updates.map((item, index) => {
                                            const { title, text, imageUrl, url } = item;
                                            return (
                                                <React.Fragment key={index}>
                                                    <UpdatesCard
                                                        title={title}
                                                        text={text}
                                                        imageUrl={imageUrl}
                                                        url={url}
                                                    />
                                                </React.Fragment>
                                            );
                                        })}
                                    </Cards>
                                </PageSection>
                            </Scroll.Element>
                        </section>
                    ) : null}
                </>
            }
            cookieNotification={<PageCookie />}
        />
    );
};

export default PageIndex;
